import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { BookOpen, Upload, FileText, X, Download } from 'lucide-react'
import { getBatches } from '@/data/mentor-mock-data'

interface StudyMaterial {
    id: string
    title: string
    subject: 'Physics' | 'Chemistry' | 'Biology'
    fileName: string
    fileSize: string
    uploadDate: string
    remarks?: string
    batches: string[]
    course?: string
}

export function StudyMaterialsView() {
    const [title, setTitle] = useState('')
    const [subject, setSubject] = useState<'Physics' | 'Chemistry' | 'Biology'>('Physics')
    const [remarks, setRemarks] = useState('')
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [selectedBatches, setSelectedBatches] = useState<string[]>([])
    const [selectedCourse, setSelectedCourse] = useState<string>('')

    // Get batches
    const batches = getBatches()
    const uniqueCourses = ['Complete NEET 2025 Preparation', 'Physics Masterclass'] // In real app, derived from mentor's assigned courses

    // Mock initial data
    const [materials, setMaterials] = useState<StudyMaterial[]>([
        {
            id: '1',
            title: 'Thermodynamics Formula Sheet',
            subject: 'Physics',
            fileName: 'thermo_formulas.pdf',
            fileSize: '2.4 MB',
            uploadDate: '2024-10-25',
            remarks: 'Important formulas for quick revision',
            batches: ['NEET 2025 Batch A'],
            course: 'Complete NEET 2025 Preparation'
        },
        {
            id: '2',
            title: 'Organic Chemistry Named Reactions',
            subject: 'Chemistry',
            fileName: 'named_reactions.pdf',
            fileSize: '1.8 MB',
            uploadDate: '2024-10-24',
            remarks: 'List of all named reactions for NEET',
            batches: ['NEET 2025 Batch A', 'NEET 2025 Batch B'],
            course: 'Complete NEET 2025 Preparation'
        }
    ])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0])
        }
    }

    const toggleBatch = (batch: string) => {
        if (selectedBatches.includes(batch)) {
            setSelectedBatches(selectedBatches.filter(b => b !== batch))
        } else {
            setSelectedBatches([...selectedBatches, batch])
        }
    }

    const handleUpload = (e: React.FormEvent) => {
        e.preventDefault()
        if (!title || !selectedFile) return

        const newMaterial: StudyMaterial = {
            id: Date.now().toString(),
            title,
            subject,
            fileName: selectedFile.name,
            fileSize: `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`,
            uploadDate: new Date().toISOString().split('T')[0],
            remarks,
            batches: selectedBatches,
            course: selectedCourse
        }

        setMaterials([newMaterial, ...materials])

        // Reset form
        setTitle('')
        setSubject('Physics')
        setRemarks('')
        setSelectedFile(null)
        setSelectedBatches([])
        setSelectedCourse('')
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-theme-primary">Study Materials</h2>
                <p className="text-sm text-theme-secondary">Upload and manage resources for students</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Upload Form */}
                <GlassmorphicCard className="p-6 h-fit">
                    <h3 className="font-bold text-theme-primary mb-4 flex items-center gap-2">
                        <Upload className="h-5 w-5 text-primary-blue" />
                        Upload New Material
                    </h3>

                    <form onSubmit={handleUpload} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-1">Subject</label>
                            <select
                                value={subject}
                                onChange={(e) => setSubject(e.target.value as any)}
                                className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-sm"
                            >
                                <option value="Physics">Physics</option>
                                <option value="Chemistry">Chemistry</option>
                                <option value="Biology">Biology</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-1">Title / Name</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Chapter 4 Notes"
                                className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-1">Course</label>
                            <select
                                value={selectedCourse}
                                onChange={(e) => setSelectedCourse(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-sm"
                            >
                                <option value="">Select Course</option>
                                {uniqueCourses.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-2">Assign to Batches</label>
                            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto custom-scrollbar p-1">
                                {batches.map(batch => (
                                    <label key={batch} className={`flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded-lg border transition-all ${selectedBatches.includes(batch) ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-600' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-theme-secondary'}`}>
                                        <input type="checkbox" checked={selectedBatches.includes(batch)} onChange={() => toggleBatch(batch)} className="hidden" />
                                        <span className="text-xs font-medium">{batch}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-1">File</label>
                            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer relative">
                                <input
                                    type="file"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                {selectedFile ? (
                                    <div className="flex items-center justify-center gap-2 text-sm text-green-600">
                                        <FileText className="h-4 w-4" />
                                        <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                                    </div>
                                ) : (
                                    <div className="text-sm text-theme-secondary">
                                        <Upload className="h-6 w-6 mx-auto mb-2 text-slate-400" />
                                        <p>Click or drag file to upload</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-theme-secondary mb-1">Remarks (Optional)</label>
                            <textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Any specific instructions..."
                                rows={3}
                                className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:border-primary-blue text-sm resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={!title || !selectedFile}
                            className="w-full py-2 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Upload Material
                        </button>
                    </form>
                </GlassmorphicCard>

                {/* Materials List */}
                <div className="lg:col-span-2 space-y-4">
                    {materials.map((material) => (
                        <GlassmorphicCard key={material.id} className="p-4 flex items-start gap-4 group">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${material.subject === 'Physics' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-600' :
                                material.subject === 'Chemistry' ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-600' :
                                    'bg-green-100 dark:bg-green-900/20 text-green-600'
                                }`}>
                                <BookOpen className="h-6 w-6" />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="font-bold text-theme-primary truncate">{material.title}</h3>
                                        <div className="flex flex-wrap items-center gap-2 text-xs text-theme-secondary mt-1">
                                            <span className={`px-2 py-0.5 rounded ${material.subject === 'Physics' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' :
                                                material.subject === 'Chemistry' ? 'bg-purple-50 text-purple-600 dark:bg-purple-900/20' :
                                                    'bg-green-50 text-green-600 dark:bg-green-900/20'
                                                } font-medium`}>
                                                {material.subject}
                                            </span>
                                            <span>•</span>
                                            <span>{material.fileSize}</span>
                                            <span>•</span>
                                            <span>{material.uploadDate}</span>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {material.batches.map(b => (
                                                <span key={b} className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs rounded border border-slate-200 dark:border-slate-700">
                                                    {b}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button className="p-2 text-slate-400 hover:text-primary-blue hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                                            <Download className="h-5 w-5" />
                                        </button>
                                        <button className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                                            <X className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>

                                {material.remarks && (
                                    <p className="text-sm text-theme-secondary mt-3 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-800">
                                        "{material.remarks}"
                                    </p>
                                )}
                            </div>
                        </GlassmorphicCard>
                    ))}

                    {materials.length === 0 && (
                        <div className="text-center py-12 text-theme-secondary">
                            <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-20" />
                            <p>No materials uploaded yet</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
